import { productApi } from "../api";

const ProductService = {
    /**
     * Obtiene todos los productos sin paginación.
     * GET /api/products/all
     */
    getAllProducts: async () => {
        try {
            const response = await productApi.fetchCustom("/all");
            return response;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Búsqueda de catálogo paginada (texto + categoría/subcategoría),
     * solo productos activos. Para pickers de UI.
     * POST /api/products/search
     */
    searchCatalog: async (
        filter: { search?: string; categoryId?: number; subCategoryId?: number },
        page = 0,
        size = 10
    ) => {
        const response = await productApi.addCustom(
            `/search?page=${page}&size=${size}&sort=name,asc`,
            filter
        );
        return response.data;
    },

    /**
     * Obtiene productos en formato dropdown.
     * GET /api/products/dropdown
     */
    getForDropdown: async () => {
        try {
            const response = await productApi.fetchCustom("/dropdown");
            return response;
        } catch (error) {
            throw error;
        }
    },

    /**
    * Obtiene productos para dropdown filtrados por tipo de ítem.
    * Se filtra por el código del ItemType (1=bienes, 2=servicios, 3=ambos, 4=otros).
    * GET /api/products/dropdown/by-item-type?itemType={code}
    *
    * @param itemType Código del tipo de ítem.
    * @returns Lista de productos filtrados para el dropdown.
    */
    getProductsForDropdownByItemType: async (itemType: number) => {
        try {
            const response = await productApi.fetchCustom(`/dropdown/by-item-type?itemType=${itemType}`);
            return response;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Obtiene todos los productos con paginación.
     * GET /api/products/list?page=0&size=10
     */
    getAllProductsPaged: async (page: number, size: number) => {
        try {
            const response = await productApi.fetchCustom(`/list?page=${page}&size=${size}`);
            return response;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Busca productos por nombre o código con paginación.
     * GET /api/products/search-paged?query=xyz&page=0&size=10
     */
    searchProductsPaged: async (query: string, page: number, size: number) => {
        try {
            const response = await productApi.fetchCustom(`/search-paged?query=${encodeURIComponent(query)}&page=${page}&size=${size}`);
            return response;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Busca productos por nombre.
     * GET /api/products/search-by-name?name=xyz
     */
    findByName: async (name: string) => {
        try {
            const response = await productApi.fetchCustom(`/search-by-name?name=${encodeURIComponent(name)}`);
            return response;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Obtiene un producto por ID.
     * GET /api/products/{id}
     */
    getProductById: async (id: number) => {
        try {
            const response = await productApi.fetchCustom(`/${id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
 * Crea un nuevo producto con imagen.
 * POST /api/products
 */
    createProduct: async (data: Object, file?: File) => {
        try {
            const response = await productApi.postFormData(data, file);
            return response.data;
        } catch (error) {
            throw error;
        }
    },


    /**
  * Actualiza un producto por ID con o sin imagen.
  * PUT /api/products/{id}
  */
    updateProduct: async (id: number, data: Object, file?: File) => {
        try {
            const response = await productApi.putFormData(id, data, file);
            return response.data;
        } catch (error) {
            throw error;
        }
    },



    /**
     * Activa o desactiva un producto por ID.
     * PATCH /api/products/{id}/toggle-status
     */
    toggleProductStatus: async (id: number) => {
        try {
            const response = await productApi.patchCustom(`${id}/toggle-status`);
            return response;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Obtiene la imagen de un producto por nombre de archivo.
     * GET /api/products/image/{filename}
     */
    getProductImage: async (filename: string): Promise<string | null> => {
        try {
            const response = await productApi.fetchBlob(`/image/${filename}`);

            // Crear un Blob URL a partir de la respuesta
            const imageUrl = URL.createObjectURL(response.data);
            return imageUrl;
        } catch (error) {
            console.error("Error obteniendo la imagen del producto:", error);
            return null;
        }
    },

    /**
     * Obtiene información de inventario del producto.
     * GET /api/products/{id}/inventory-info
     */
    getInventoryInfo: async (id: number) => {
        try {
            const response =
                await productApi.fetchCustom(
                    `/${id}/inventory-info`
                );

            return response.data;
        } catch (error) {
            throw error;
        }
    },

};

export default ProductService;
